import type { SuggestionEventItem } from "@/domain/suggestions";
import { getSuggestionEventsSink } from "@/domain/suggestions";
import { getMerchantId } from "@/lib/company-storage";
import type { Cart } from "@/types";

/**
 * Fires one `CONVERSION` event per surface for a successfully placed order,
 * carrying the catalog ids of the lines that were `addedVia` a suggestion.
 * Lines added the regular way (`addedVia` unset) never enter this — a forged
 * or over-eager event cannot inflate the attach rate. Never throws, so a
 * confirmed order can never be reported as a checkout failure.
 */
export function recordSuggestionConversions(cart: Cart, orderId: string): void {
  try {
    enqueueConversions(cart, orderId);
  } catch {
    return;
  }
}

function enqueueConversions(cart: Cart, orderId: string): void {
  const merchantId = getMerchantId();
  if (!merchantId) return;

  const itemsBySurface = new Map<"CART" | "ITEM" | "DAYPART", Map<string, SuggestionEventItem>>();
  for (const line of cart.items) {
    if (!line.addedVia) continue;
    const itemId = line.itemOffer.itemId;
    const items = itemsBySurface.get(line.addedVia) ?? new Map<string, SuggestionEventItem>();
    items.set(itemId, {
      itemId,
      arm: line.addedViaArm ?? "EXPLOIT",
      ...(line.addedViaAnchorItemId ? { anchorItemId: line.addedViaAnchorItemId } : {}),
    });
    itemsBySurface.set(line.addedVia, items);
  }

  if (itemsBySurface.size === 0) return;

  const sink = getSuggestionEventsSink(merchantId);
  for (const [surface, items] of itemsBySurface) {
    const values = [...items.values()];
    sink.enqueue({ type: "CONVERSION", surface, itemIds: values.map((item) => item.itemId), items: values, orderId });
  }
}
